import { useEffect } from 'react'
import { SITE_NAME, SOCIAL_IMAGE_URL } from '../../config/site'
import { createStructuredData, getCanonicalUrl, getSeoRoute, INDEX_ROBOTS, NO_INDEX_ROBOTS, type SeoRoutePath } from '../../seo/routes'
import { getBlogPost } from '../../blog/registry'

type SeoProps =
  | { route: SeoRoutePath }
  | { noIndex: true; title?: string; description?: string; path?: string }
  | { title: string; description: string; path: string }

function setMeta(name: string, content: string, property = false) {
  const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`
  let element = document.head.querySelector<HTMLMetaElement>(selector)

  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(property ? 'property' : 'name', name)
    document.head.append(element)
  }

  element.content = content
}

function removeMeta(name: string, property = false) {
  const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`
  document.head.querySelector(selector)?.remove()
}

export function Seo(props: SeoProps) {
  const noIndex = 'noIndex' in props
  const legacyPath = 'path' in props && typeof props.path === 'string' ? `${props.path.replace(/\/$/, '')}/` : null
  const routePath = 'route' in props ? props.route : legacyPath
  const explicitTitle = 'title' in props ? props.title : undefined
  const explicitDescription = 'description' in props ? props.description : undefined

  useEffect(() => {
    const route = noIndex || !routePath ? null : getSeoRoute(routePath as SeoRoutePath)
    const title = route?.title ?? explicitTitle ?? 'Page not found — StayInBrowser'
    const description = route?.description ?? explicitDescription ?? 'This StayInBrowser page could not be found.'
    const socialUrl = route
      ? getCanonicalUrl(route.path)
      : noIndex && legacyPath ? getCanonicalUrl(legacyPath) : ''

    document.title = title
    setMeta('description', description)
    setMeta('robots', noIndex ? NO_INDEX_ROBOTS : INDEX_ROBOTS)
    setMeta('googlebot', noIndex ? NO_INDEX_ROBOTS : INDEX_ROBOTS)
    setMeta('og:title', title, true)
    setMeta('og:description', description, true)

    if (socialUrl) setMeta('og:url', socialUrl, true)
    else removeMeta('og:url', true)
    setMeta('og:site_name', SITE_NAME, true)
    setMeta('og:locale', 'en_US', true)

    setMeta('og:image:width', '1200', true)
    setMeta('og:image:height', '630', true)
    setMeta('og:image:alt', 'StayInBrowser — your files stay on your device', true)
    setMeta('twitter:card', 'summary_large_image')
    setMeta('twitter:title', title)
    setMeta('twitter:description', description)


    setMeta('og:type', route && route.path.startsWith('/blog/') && route.path !== '/blog/' ? 'article' : 'website', true)

    if (route && route.path.startsWith('/blog/') && route.path !== '/blog/') {
      const postSlug = route.path.replace('/blog/', '').replace('/', '')
      const post = getBlogPost(postSlug)
      if (post) {
        setMeta('article:published_time', post.publishedDate, true)
        if (post.updatedDate) setMeta('article:modified_time', post.updatedDate, true)
        setMeta('article:section', post.category, true)

        const imageUrl = post.heroImage ? (post.heroImage.startsWith('http') ? post.heroImage : `https://stayinbrowser.com${post.heroImage}`) : SOCIAL_IMAGE_URL
        setMeta('og:image', imageUrl, true)
        setMeta('twitter:image', imageUrl)
        setMeta('og:image:alt', post.imageAlt || title, true)
        setMeta('twitter:image:alt', post.imageAlt || title)
      }
    } else {
      setMeta('og:image', SOCIAL_IMAGE_URL, true)
      setMeta('twitter:image', SOCIAL_IMAGE_URL)
      setMeta('og:image:alt', 'StayInBrowser — your files stay on your device', true)
      setMeta('twitter:image:alt', 'StayInBrowser — your files stay on your device')
    }


    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
    if (route && !canonical) {
      canonical = document.createElement('link')
      canonical.rel = 'canonical'
      document.head.append(canonical)
    }
    if (route && canonical) canonical.href = socialUrl
    if (!route && canonical) canonical.remove()

    let structuredData = document.head.querySelector<HTMLScriptElement>('#stay-in-browser-structured-data')
    if (route && !structuredData) {
      structuredData = document.createElement('script')
      structuredData.id = 'stay-in-browser-structured-data'
      structuredData.type = 'application/ld+json'
      document.head.append(structuredData)
    }
    if (route && structuredData) structuredData.text = JSON.stringify(createStructuredData(route))
    if (!route && structuredData) structuredData.remove()
  }, [explicitDescription, explicitTitle, legacyPath, noIndex, routePath])

  return null
}
